import React, { Suspense, useCallback, useMemo, useState, useRef, useLayoutEffect } from 'react';
import useDebouncedValue from 'util/use-debounced-value';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { usePathParam } from 'react-resource-router';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { getCloudId } from '@atlassian/help-center-common-util/meta';
import {
    ALL_RESULTS_COUNT,
    DEFAULT_RESULTS_COUNT,
    SEARCH_QUERY_DEBOUNCE_TIME,
    useQueryParams,
    getIdsArray,
    isSinglePortalSearch,
} from '../common';
import { SearchResultsLoader } from '../loading';
import { SearchResultNone } from '../none';
import { Resources } from '../resources';
import type { searchResultQuery } from './__generated__/searchResultQuery.graphql';

export interface Props {
    term: string;
    projectId?: number;
    showAllResults?: boolean;
}

type SearchResultProps = Props & WithAnalyticsEventsProps;

export const SearchResult = ({ term, projectId, showAllResults }: SearchResultProps) => {
    const cloudId = useMemo(() => getCloudId(), []);
    // Suppressing existing violation. Please fix this.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { portalIds, categoryIds, resultLimit = DEFAULT_RESULTS_COUNT } = useQueryParams();
    const [portalId] = usePathParam('portalId');
    const [totalCount, setTotalCount] = useState(-1);
    const resultsCount = useRef(-1);
    const debouncedTerm = useDebouncedValue(term, SEARCH_QUERY_DEBOUNCE_TIME);
    const getSelectedPortalIds = () => {
        if (isSinglePortalSearch(portalId) && showAllResults) {
            return getIdsArray(projectId);
        }
        if (isSinglePortalSearch(portalId) && !showAllResults) {
            return getIdsArray(portalId);
        }
        return getIdsArray(portalIds);
    };

    const data = useLazyLoadQuery<searchResultQuery>(
        graphql`
            query searchResultQuery(
                $cloudId: ID!
                $queryTerm: String!
                $resultLimit: Int
                $portalIds: [ID!]
                $categoryIds: [ID!]
            ) {
                ...resourcesFragment
            }
        `,
        {
            cloudId,
            queryTerm: debouncedTerm,
            portalIds: getSelectedPortalIds(),
            categoryIds: getIdsArray(categoryIds),
            // Suppressing existing violation. Please fix this.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            resultLimit: showAllResults ? ALL_RESULTS_COUNT : resultLimit,
        },
        { fetchPolicy: 'store-or-network' }
    );

    const updateResultsCount = useCallback((count: number) => {
        if (resultsCount.current < 0) resultsCount.current = 0;
        resultsCount.current += count;
        setTotalCount(resultsCount.current);
    }, []);

    useLayoutEffect(() => {
        // Reset count to initial state on term change
        resultsCount.current = -1;
        setTotalCount(-1);
    }, [debouncedTerm]);

    if (!data) {
        return null;
    }

    return (
        <Suspense fallback={<SearchResultsLoader />}>
            {totalCount === 0 ? (
                <SearchResultNone term={debouncedTerm} />
            ) : (
                <Resources
                    term={debouncedTerm}
                    result={data}
                    showAllResults={showAllResults}
                    updateResultsCount={updateResultsCount}
                />
            )}
        </Suspense>
    );
};
